import { photoByKey, publicPhotoPath } from '../renderers/archive-act';
import { localeText } from '../locale';
import type { PhotoSpec } from './primitives';
import { storyPlaybackDelay } from './story-playback';

export interface StoryArtwork {
  src: string;
  alt: string;
  width: number;
  height: number;
  label?: string;
}

export interface StoryFact {
  label: string;
  value: string;
  note?: string;
}

export interface StoryLink {
  label: string;
  href: string;
}

export interface StorySlideSpec {
  photo?: PhotoSpec;
  format?: 'photo' | 'folio' | 'facts';
  mark?: string;
  artwork?: readonly StoryArtwork[];
  facts?: readonly StoryFact[];
  links?: readonly StoryLink[];
  eyebrow?: string;
  title?: string;
  text?: string;
  autoAdvance?: boolean;
}

export interface StoriesGroup {
  id: string;
  title: string;
  coverPhoto: PhotoSpec;
  slides: StorySlideSpec[];
}

export interface StoriesViewerShowOptions {
  startGroupIndex?: number;
  returnFocusTo?: HTMLElement;
}

export interface StoriesViewer {
  element: HTMLElement;
  show(options?: StoriesViewerShowOptions): void;
  hide(): void;
}

interface StoriesViewerOptions {
  onExit?: (group: StoriesGroup) => void;
}

type TimerHandle = ReturnType<typeof setTimeout>;

function slideImageData(photo: PhotoSpec | undefined): { src: string; srcset: string; width: number; height: number } | null {
  if (!photo) return null;
  const record = photoByKey.get(photo.key);
  if (!record) return null;
  const full = record.v[record.v.length - 1];
  const fallback = record.v.find(({ width }) => width >= 1280) ?? full;
  const variant = fallback ?? full;
  if (!variant) return null;
  return {
    src: publicPhotoPath(variant.src),
    srcset: record.v.map((item) => `${publicPhotoPath(item.src)} ${item.width}w`).join(', '),
    width: variant.width,
    height: variant.height,
  };
}

export function createStoriesViewer(
  groups: StoriesGroup[],
  options: StoriesViewerOptions = {},
): StoriesViewer | null {
  const validGroups = groups.filter((g) => g.slides.length > 0);
  if (!validGroups.length) return null;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const overlay = document.createElement('div');
  overlay.className = 'stories-viewer';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', localeText('Сториз', 'Тарыхча'));
  overlay.hidden = true;

  // Progress bars — rebuilt per current group
  const progress = document.createElement('div');
  progress.className = 'stories-viewer__progress';
  overlay.append(progress);

  // Slide stage
  const stage = document.createElement('div');
  stage.className = 'stories-viewer__stage';

  // Stage image (real <img> — not CSS background, so relative URLs resolve against the document)
  const stageImg = document.createElement('img');
  stageImg.className = 'stories-viewer__img';
  stageImg.alt = '';
  stageImg.loading = 'eager';
  stageImg.decoding = 'async';
  const editorialVisual = document.createElement('div');
  editorialVisual.className = 'stories-viewer__editorial';
  editorialVisual.hidden = true;
  stage.append(stageImg, editorialVisual);

  // Text overlay — eyebrow, title, body
  const textOverlay = document.createElement('div');
  textOverlay.className = 'stories-viewer__text';

  const eyebrowEl = document.createElement('p');
  eyebrowEl.className = 'stories-viewer__eyebrow';

  const titleEl = document.createElement('h3');
  titleEl.className = 'stories-viewer__title';

  const bodyEl = document.createElement('p');
  bodyEl.className = 'stories-viewer__body';

  const chapterCta = document.createElement('button');
  chapterCta.type = 'button';
  chapterCta.className = 'stories-viewer__chapter-cta';
  chapterCta.textContent = localeText('Читать главу полностью', 'Бөлүмдү толук окуу');

  textOverlay.append(eyebrowEl, titleEl, bodyEl, chapterCta);

  // Group label (chapter name, top-left)
  const groupLabel = document.createElement('span');
  groupLabel.className = 'stories-viewer__group-label';

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'stories-viewer__close';
  closeBtn.setAttribute('aria-label', localeText('Закрыть', 'Жабуу'));
  closeBtn.innerHTML = '<svg aria-hidden="true" viewBox="0 0 32 32" width="32" height="32"><path d="M9 9 23 23M23 9 9 23" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square"/></svg>';

  const pauseBtn = document.createElement('button');
  pauseBtn.type = 'button';
  pauseBtn.className = 'stories-viewer__pause';

  // Tap navigation zones
  const prevZone = document.createElement('button');
  prevZone.type = 'button';
  prevZone.className = 'stories-viewer__nav stories-viewer__nav--prev';
  prevZone.setAttribute('aria-label', localeText('Предыдущая', 'Мурунку'));

  const nextZone = document.createElement('button');
  nextZone.type = 'button';
  nextZone.className = 'stories-viewer__nav stories-viewer__nav--next';
  nextZone.setAttribute('aria-label', localeText('Следующая', 'Кийинки'));

  stage.append(prevZone, nextZone, closeBtn, pauseBtn, groupLabel, textOverlay);

  // Highlights tray
  const tray = document.createElement('div');
  tray.className = 'stories-viewer__tray';
  tray.setAttribute('role', 'tablist');
  tray.setAttribute('aria-label', localeText('Главы', 'Бөлүмдөр'));

  const trayButtons: HTMLButtonElement[] = [];
  for (const [groupIndex, group] of validGroups.entries()) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'stories-viewer__tray-item';
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-label', group.title);
    btn.dataset.groupIndex = String(groupIndex);

    const coverImg = slideImageData(group.coverPhoto);
    const ring = document.createElement('span');
    ring.className = 'stories-viewer__tray-ring';
    const img = document.createElement('img');
    if (coverImg) {
      img.src = coverImg.src;
      img.srcset = coverImg.srcset;
      img.sizes = '88px';
      img.alt = '';
      img.loading = 'lazy';
    }
    const label = document.createElement('span');
    label.className = 'stories-viewer__tray-label';
    label.textContent = group.title;

    ring.append(img);
    btn.append(ring, label);
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      switchGroup(groupIndex, 0);
    });
    trayButtons.push(btn);
    tray.append(btn);
  }

  overlay.append(progress, stage, tray);

  let currentGroupIndex = 0;
  let currentSlideIndex = 0;
  let fills: HTMLElement[] = [];
  let autoTimer: TimerHandle | undefined;
  let active = false;
  let returnFocus: HTMLElement | null = null;
  let paused = false;

  function rebuildProgressBars(): void {
    progress.replaceChildren();
    fills = [];
    const group = validGroups[currentGroupIndex];
    if (!group) return;
    for (let i = 0; i < group.slides.length; i++) {
      const bar = document.createElement('span');
      bar.className = 'stories-viewer__progress-bar';
      const fill = document.createElement('span');
      fill.className = 'stories-viewer__progress-fill';
      bar.append(fill);
      fills.push(fill);
      progress.append(bar);
    }
  }

  function updateTrayState(): void {
    for (const [i, btn] of trayButtons.entries()) {
      const isCurrent = i === currentGroupIndex;
      btn.setAttribute('aria-selected', String(isCurrent));
      btn.classList.toggle('is-active', isCurrent);
    }
    const activeBtn = trayButtons[currentGroupIndex];
    if (!activeBtn) return;
    const centeredLeft = activeBtn.offsetLeft + activeBtn.offsetWidth / 2 - tray.clientWidth / 2;
    tray.scrollLeft = Math.max(0, centeredLeft);
  }
  function renderEditorialVisual(slide: StorySlideSpec): boolean {
    editorialVisual.replaceChildren();
    const isEditorial = slide.format === 'folio' || slide.format === 'facts';
    if (!isEditorial) return false;

    if (slide.mark) {
      const mark = document.createElement('strong');
      mark.className = 'stories-viewer__editorial-mark';
      mark.textContent = slide.mark;
      editorialVisual.append(mark);
    }

    if (slide.artwork?.length) {
      const artwork = document.createElement('div');
      artwork.className = 'stories-viewer__editorial-artwork';
      for (const item of slide.artwork) {
        const figure = document.createElement('figure');
        const img = document.createElement('img');
        img.src = item.src;
        img.alt = item.alt;
        img.width = item.width;
        img.height = item.height;
        img.loading = 'eager';
        figure.append(img);
        if (item.label) {
          const caption = document.createElement('figcaption');
          caption.textContent = item.label;
          figure.append(caption);
        }
        artwork.append(figure);
      }
      editorialVisual.append(artwork);
    }

    if (slide.facts?.length) {
      const facts = document.createElement('div');
      facts.className = 'stories-viewer__editorial-facts';
      for (const item of slide.facts) {
        const fact = document.createElement('article');
        const label = document.createElement('span');
        label.textContent = item.label;
        const value = document.createElement('strong');
        value.textContent = item.value;
        fact.append(label, value);
        if (item.note) {
          const note = document.createElement('p');
          note.textContent = item.note;
          fact.append(note);
        }
        facts.append(fact);
      }
      editorialVisual.append(facts);
    }

    if (slide.links?.length) {
      const links = document.createElement('nav');
      links.className = 'stories-viewer__editorial-links';
      links.setAttribute('aria-label', localeText('Источники', 'Булактар'));
      for (const item of slide.links) {
        const link = document.createElement('a');
        link.href = item.href;
        link.target = '_blank';
        link.rel = 'noreferrer';
        link.textContent = `${item.label} ↗`;
        links.append(link);
      }
      editorialVisual.append(links);
    }
    return true;
  }


  function renderSlide(animateProgress: boolean): void {
    const group = validGroups[currentGroupIndex];
    if (!group) return;
    const slide = group.slides[currentSlideIndex];
    if (!slide) return;

    const isEditorial = renderEditorialVisual(slide);
    const imgData = isEditorial ? null : slideImageData(slide.photo);
    stageImg.hidden = isEditorial;
    editorialVisual.hidden = !isEditorial;
    stage.classList.toggle('is-editorial-slide', isEditorial);
    stage.dataset.slideFormat = slide.format ?? 'photo';
    if (imgData) {
      stageImg.src = imgData.src;
      stageImg.srcset = imgData.srcset;
      stageImg.width = imgData.width;
      stageImg.height = imgData.height;
      stageImg.sizes = '(max-width: 700px) 100vw, 80vw';
    } else if (!isEditorial) {
      stageImg.removeAttribute('src');
      stageImg.removeAttribute('srcset');
    }

    // Text overlay
    eyebrowEl.textContent = slide.eyebrow ?? '';
    eyebrowEl.hidden = !slide.eyebrow;
    titleEl.textContent = slide.title ?? '';
    titleEl.hidden = !slide.title;
    bodyEl.textContent = slide.text ?? '';
    bodyEl.hidden = !slide.text;

    groupLabel.textContent = `${currentGroupIndex + 1} / ${validGroups.length} · ${group.title}`;
    stage.classList.toggle('has-text', Boolean(slide.text));
    updatePauseButton(slide);

    const playbackDelay = currentPlaybackDelay();
    for (const [i, fill] of fills.entries()) {
      fill.style.transition = 'none';
      fill.style.width = i < currentSlideIndex ? '100%' : '0%';
      void fill.offsetWidth;
      if (i <= currentSlideIndex) {
        if (i === currentSlideIndex && animateProgress && playbackDelay !== null) {
          fill.style.transition = `width ${playbackDelay}ms linear`;
        }
        fill.style.width = '100%';
      }
    }

    scheduleAutoAdvance();
  }

  function currentPlaybackDelay(): number | null {
    const slide = validGroups[currentGroupIndex]?.slides[currentSlideIndex];
    return storyPlaybackDelay(slide, reducedMotion, paused);
  }

  function scheduleAutoAdvance(): void {
    clearTimeout(autoTimer);
    autoTimer = undefined;
    const delay = currentPlaybackDelay();
    if (delay === null) return;
    autoTimer = setTimeout(goNext, delay);
  }

  function updatePauseButton(slide: StorySlideSpec): void {
    pauseBtn.hidden = reducedMotion || slide.autoAdvance === false;
    pauseBtn.setAttribute('aria-pressed', String(paused));
    pauseBtn.setAttribute(
      'aria-label',
      localeText(paused ? 'Продолжить' : 'Пауза', paused ? 'Улантуу' : 'Тыным'),
    );
    pauseBtn.innerHTML = paused
      ? '<svg aria-hidden="true" viewBox="0 0 32 32"><path d="m12 9 12 7-12 7Z" fill="currentColor"/></svg>'
      : '<svg aria-hidden="true" viewBox="0 0 32 32"><path d="M11 9v14M21 9v14" fill="none" stroke="currentColor" stroke-width="3"/></svg>';
  }

  function switchGroup(groupIndex: number, slideIndex: number): void {
    currentGroupIndex = groupIndex;
    currentSlideIndex = slideIndex;
    rebuildProgressBars();
    updateTrayState();
    renderSlide(true);
  }

  function goNext(): void {
    const group = validGroups[currentGroupIndex];
    if (!group) return;
    if (currentSlideIndex < group.slides.length - 1) {
      currentSlideIndex++;
      renderSlide(true);
    } else if (currentGroupIndex < validGroups.length - 1) {
      switchGroup(currentGroupIndex + 1, 0);
    } else {
      hide();
    }
  }

  function goPrev(): void {
    if (currentSlideIndex > 0) {
      currentSlideIndex--;
      renderSlide(false);
    } else if (currentGroupIndex > 0) {
      const prevGroup = validGroups[currentGroupIndex - 1];
      switchGroup(currentGroupIndex - 1, (prevGroup?.slides.length ?? 1) - 1);
    }
  }

  function show(options: StoriesViewerShowOptions = {}): void {
    if (active) return;
    active = true;
    paused = false;
    returnFocus = options.returnFocusTo ?? null;
    overlay.hidden = false;
    document.body.classList.add('stories-viewer--open');
    const startGroupIndex = Math.max(0, Math.min(options.startGroupIndex ?? 0, validGroups.length - 1));
    switchGroup(startGroupIndex, 0);
    closeBtn.focus();
  }

  function hide(): void {
    if (!active) return;
    const group = validGroups[currentGroupIndex];
    active = false;
    overlay.hidden = true;
    document.body.classList.remove('stories-viewer--open');
    clearTimeout(autoTimer);
    autoTimer = undefined;
    const focusTarget = returnFocus;
    returnFocus = null;
    focusTarget?.focus();
    if (group) options.onExit?.(group);
  }

  // Events
  prevZone.addEventListener('click', (e) => { e.preventDefault(); goPrev(); });
  nextZone.addEventListener('click', (e) => { e.preventDefault(); goNext(); });
  closeBtn.addEventListener('click', (e) => { e.preventDefault(); hide(); });
  pauseBtn.addEventListener('click', (e) => {
    e.preventDefault();
    paused = !paused;
    renderSlide(!paused);
  });
  chapterCta.addEventListener('click', (e) => {
    e.preventDefault();
    hide();
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) hide();
  });

  overlay.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.preventDefault(); hide(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
  });

  // Touch swipe
  let touchStartX = 0;
  let touchStartY = 0;
  let touchActive = false;

  stage.addEventListener('touchstart', (e) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartX = touch.clientX;
    touchStartY = touch.clientY;
    touchActive = true;
  }, { passive: true });

  stage.addEventListener('touchend', (e) => {
    if (!touchActive) return;
    touchActive = false;
    const touch = e.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - touchStartX;
    const dy = touch.clientY - touchStartY;
    if (Math.abs(dy) > Math.abs(dx) && dy > 80) {
      hide();
    } else if (Math.abs(dx) > 50) {
      if (dx > 0) goPrev();
      else goNext();
    }
  }, { passive: true });

  // Pause on hover (desktop) without changing the explicit playback choice.
  stage.addEventListener('mouseenter', () => {
    clearTimeout(autoTimer);
    autoTimer = undefined;
  });

  stage.addEventListener('mouseleave', scheduleAutoAdvance);

  return { element: overlay, show, hide };
}
